import { useState, useEffect } from 'react'
import styles from './AddGroupModal.module.css'
import apiClient from '../../utils/api'
import useEscapeKey from '../../hooks/useEscapeKey'

export default function AddGroupModal({ 
    isOpen, 
    onClose, 
    onSave,
    apiToken = null
}) {
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    })
    
    const [loading, setLoading] = useState(false)
    const [errors, setErrors] = useState({})

    // Handle escape key to close modal (disabled when loading)
    useEscapeKey(isOpen, onClose, !loading)

    // Reset form when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                name: '',
                description: ''
            })
            setErrors({})
        }
    }, [isOpen])

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }))
        
        // Clear error for this field when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: null
            }))
        }
    }

    const validateForm = () => {
        const newErrors = {}
        
        // Name validation
        if (!formData.name.trim()) {
            newErrors.name = 'Group name is required'
        } else if (formData.name.trim().length < 2) {
            newErrors.name = 'Group name must be at least 2 characters'
        } else if (formData.name.trim().length > 100) {
            newErrors.name = 'Group name must be less than 100 characters'
        }
        
        // Description validation
        if (formData.description.length > 500) {
            newErrors.description = 'Description must be less than 500 characters'
        }
        
        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        
        if (!validateForm()) {
            return
        }
        
        setLoading(true)
        
        try {
            if (!apiToken) {
                throw new Error('Authentication token is required')
            }
            
            apiClient.setAuthToken(apiToken)
            
            const groupData = {
                name: formData.name.trim(),
                description: formData.description.trim() || null
            }
            
            const response = await apiClient.createGroup(groupData)
            
            // Notify parent component of successful creation
            if (onSave) {
                onSave(response)
            }
            
            onClose()
        } catch (error) {
            console.error('Failed to create group:', error)
            
            // Handle validation errors from backend
            if (error.message.includes('details')) {
                const errorMessage = error.message
                
                // Try to extract specific field errors from backend
                if (errorMessage.includes('Name')) {
                    setErrors({ name: 'This group name is already taken or invalid' })
                } else {
                    setErrors({ general: error.message })
                }
            } else {
                setErrors({ 
                    general: error.message || 'Failed to create group. Please try again.' 
                })
            }
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        if (!loading) {
            onClose()
        }
    }

    if (!isOpen) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Add New Group</h2>
                    <button 
                        className={styles.closeButton}
                        onClick={handleCancel}
                        aria-label="Close modal"
                        disabled={loading}
                    >
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    {/* General Error Display */}
                    {errors.general && (
                        <div className={styles.errorMessage}>
                            {errors.general}
                        </div>
                    )}

                    {/* Group Name */}
                    <div className={styles.formGroup}>
                        <label htmlFor="groupName" className={styles.label}>
                            Group Name *
                        </label>
                        <input
                            type="text"
                            id="groupName"
                            className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            placeholder="Enter group name"
                            maxLength="100"
                            autoFocus
                            disabled={loading}
                        />
                        {errors.name && (
                            <span className={styles.fieldError}>{errors.name}</span>
                        )}
                    </div>

                    {/* Description */}
                    <div className={styles.formGroup}>
                        <label htmlFor="description" className={styles.label}>
                            Description
                        </label>
                        <textarea
                            id="description"
                            className={`${styles.textarea} ${errors.description ? styles.inputError : ''}`}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder="Enter group description (optional)"
                            rows="4"
                            maxLength="500"
                            disabled={loading}
                        />
                        {errors.description && (
                            <span className={styles.fieldError}>{errors.description}</span>
                        )}
                        <div className={styles.characterCount}>
                            {formData.description.length}/500 characters
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className={styles.buttonRow}>
                        <div className={styles.rightButtons}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={styles.saveButton}
                                disabled={loading}
                            >
                                {loading ? 'Creating...' : 'Add Group'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
}